// pages/OrganisationLimits.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminPermissions } from "../context/AdminContext";
import limitsService from "../services/limitService";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  Button,
  Input,
  Select,
  Space,
  message,
  Spin,
  Alert,
} from "antd";
import {
  DollarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;

const OrganisationLimits = () => {
  const navigate = useNavigate();
  const { hasRouteAccess, canPerformAction } = useAdminPermissions();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [limits, setLimits] = useState([]);
  const [filteredLimits, setFilteredLimits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLevel, setFilterLevel] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Vérifier les permissions
  useEffect(() => {
    if (!hasRouteAccess("/organisations/limits")) {
      message.error("Accès refusé à cette ressource");
      navigate("/dashboard");
    }
  }, [hasRouteAccess, navigate]);

  // Charger les données
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsResponse, limitsResponse] = await Promise.all([
        limitsService.getGlobalStats(),
        limitsService.getAllLimits(),
      ]);

      setStats(statsResponse.data);
      setLimits(limitsResponse.data);
      setFilteredLimits(limitsResponse.data);
    } catch (error) {
      message.error("Erreur lors du chargement des données");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage
  useEffect(() => {
    let result = [...limits];

    // Filtre par recherche
    if (searchTerm) {
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.organisation_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Filtre par niveau
    if (filterLevel !== "all") {
      result = result.filter((item) => item.verification_level === filterLevel);
    }

    // Filtre par statut
    if (filterStatus !== "all") {
      if (filterStatus === "active") {
        result = result.filter((item) => item.is_active === 1);
      } else if (filterStatus === "inactive") {
        result = result.filter((item) => item.is_active === 0);
      } else if (filterStatus === "warning") {
        result = result.filter(
          (item) => item.daily.percentage > 80 || item.monthly.percentage > 80,
        );
      } else if (filterStatus === "exceeded") {
        result = result.filter(
          (item) =>
            item.daily.percentage >= 100 || item.monthly.percentage >= 100,
        );
      }
    }

    setFilteredLimits(result);
  }, [searchTerm, filterLevel, filterStatus, limits]);

  // Formater les montants
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
  };

  // Obtenir la couleur du tag selon le niveau
  const getLevelColor = (level) => {
    return level === "level_2" ? "green" : "blue";
  };

  // Obtenir la couleur de la progression
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "#ff4d4f";
    if (percentage >= 80) return "#faad14";
    return "#52c41a";
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Organisation",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 200,
      render: (text, record) => (
        <div>
          <div className="font-semibold">{text}</div>
          <div className="text-xs text-gray-500">
            {record.organisation_id.substring(0, 8)}...
          </div>
        </div>
      ),
    },
    {
      title: "Niveau",
      dataIndex: "verification_level",
      key: "verification_level",
      width: 100,
      render: (level) => (
        <Tag color={getLevelColor(level)}>
          {level === "level_2" ? "LEVEL 2" : "LEVEL 1"}
        </Tag>
      ),
    },
    {
      title: "Limite Journalière",
      key: "daily",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="text-sm">
            {formatAmount(record.daily.used)} /{" "}
            {formatAmount(record.daily.limit)}
          </div>
          <Progress
            percent={Math.round(record.daily.percentage)}
            strokeColor={getProgressColor(record.daily.percentage)}
            size="small"
          />
        </div>
      ),
    },
    {
      title: "Limite Mensuelle",
      key: "monthly",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="text-sm">
            {formatAmount(record.monthly.used)} /{" "}
            {formatAmount(record.monthly.limit)}
          </div>
          <Progress
            percent={Math.round(record.monthly.percentage)}
            strokeColor={getProgressColor(record.monthly.percentage)}
            size="small"
          />
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive === 1 ? "success" : "default"}>
          {isActive === 1 ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() =>
            navigate(`/organisations/${record.organisation_id}/limits`)
          }
        >
          Détails
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Limites de Transaction</h1>
        <p className="text-gray-500">
          Gestion des limites de transaction pour toutes les organisations
        </p>
      </div>

      {/* Statistiques */}
      {stats && (
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Organisations"
                value={stats.total_organisations}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Limites Actives"
                value={stats.active_limits}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Dépassement Journalier"
                value={stats.over_daily_limit}
                prefix={<WarningOutlined />}
                valueStyle={{
                  color: stats.over_daily_limit > 0 ? "#cf1322" : "#3f8600",
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Dépassement Mensuel"
                value={stats.over_monthly_limit}
                prefix={<WarningOutlined />}
                valueStyle={{
                  color: stats.over_monthly_limit > 0 ? "#cf1322" : "#3f8600",
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtres */}
      <Card className="mb-4">
        <Space size="middle" wrap>
          <Search
            placeholder="Rechercher par nom ou ID..."
            allowClear
            style={{ width: 300 }}
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
          />

          <Select
            value={filterLevel}
            onChange={setFilterLevel}
            style={{ width: 150 }}
          >
            <Option value="all">Tous les niveaux</Option>
            <Option value="level_1">Level 1</Option>
            <Option value="level_2">Level 2</Option>
          </Select>

          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 180 }}
          >
            <Option value="all">Tous les statuts</Option>
            <Option value="active">Actif</Option>
            <Option value="inactive">Inactif</Option>
            <Option value="warning">Alerte (&gt;80%)</Option>
            <Option value="exceeded">Dépassé</Option>
          </Select>

          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Actualiser
          </Button>
        </Space>
      </Card>

      {/* Alerte si dépassements */}
      {stats &&
        (stats.over_daily_limit > 0 || stats.over_monthly_limit > 0) && (
          <Alert
            message="Attention"
            description={`${stats.over_daily_limit} organisation(s) ont dépassé leur limite journalière et ${stats.over_monthly_limit} leur limite mensuelle.`}
            type="warning"
            showIcon
            className="mb-4"
          />
        )}

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredLimits}
          rowKey="organisation_id"
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 20,
            showTotal: (total) => `Total: ${total} organisation(s)`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Card>
    </div>
  );
};

export default OrganisationLimits;
